import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth, API_URL } from '../utils/api';
import { Building2, Edit, X, MapPin, Phone, Mail, Eye, Save, Clock, Upload, Trash2, AlertTriangle } from 'lucide-react';
import { agentHealthy, listAgentPrinters } from '../utils/printAgent';
import { DEFAULT_VAT_RATE } from '../utils/vatConfig';

const Branches = ({ user, currentBranch, onBranchUpdate, onCurrentBranchUpdate }) => {
  const { print_lang } = useLanguage();
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view' or 'edit'
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  // Save failures show as an in-page banner in the modal — never window.alert,
  // which cannot be translated and shows the API's raw English. Same pattern
  // as DrugLabelSettings.js and PatientRecord.js.
  const [formError, setFormError] = useState('');

  // Printers installed on THIS PC, as reported by the local print agent.
  const [agentPrinters, setAgentPrinters] = useState([]);
  const [agentUp, setAgentUp] = useState(false);
  // null until the probe answers. Kept separate from agentUp so the warning
  // below is not shown during the second-and-a-half the health check takes —
  // a red banner that appears on every open and then vanishes trains staff to
  // ignore it.
  const [agentProbed, setAgentProbed] = useState(false);

  // The dropdown replaces the text box only when the agent actually reported
  // printers. An agent running on a PC with none installed — or one whose
  // /printers call timed out — would otherwise leave an admin with a list
  // containing nothing but "Use the browser print dialog" and no way to type a
  // name at all, which is worse than no agent.
  const usePrinterDropdown = agentUp && agentPrinters.length > 0;

  useEffect(() => {
    // One probe on mount. The agent is per-PC, so this is about THIS front desk:
    // the dropdown can only list what this machine can print to. When it is not
    // running, the field stays free text so a PC without the agent can still
    // record the name a printing PC will use.
    let cancelled = false;
    agentHealthy().then((ok) => {
      if (cancelled) return;
      setAgentUp(ok);
      setAgentProbed(true);
      if (ok) listAgentPrinters().then((list) => { if (!cancelled) setAgentPrinters(list); });
    });
    return () => { cancelled = true; };
  }, []);

  // Logo upload states
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const logoInputRef = useRef(null);

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_URL}/${path}`;
  };

  const resetLogoStates = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogo(false);
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    setIsLoading(true);
    try {
      const data = await fetchWithAuth('/api/branches');
      setBranches(data || []);
    } catch (error) {
      console.error('Failed to fetch branches:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = (branch) => {
    setSelectedBranch(branch);
    resetLogoStates();
    setModalMode('view');
    setShowModal(true);
  };

  const handleEdit = (branch) => {
    setSelectedBranch(branch);
    setFormData({
      name: branch.name || '',
      address: branch.address || '',
      phone: branch.phone || '',
      email: branch.email || '',
      manager: branch.manager || '',
      vat_number: branch.vat_number || '',
      business_license: branch.business_license || '',
      dental_license: branch.dental_license || '',
      open_time: branch.open_time || '',
      close_time: branch.close_time || '',
      open_days: branch.open_days || '',
      language: branch.language || 'th',
      smartcard_enabled: branch.smartcard_enabled || false,
      vat_rate: branch.vat_rate ?? DEFAULT_VAT_RATE,
      mobile_phone: branch.mobile_phone || '',
      receipt_printer_name: branch.receipt_printer_name || '',
      label_printer_name: branch.label_printer_name || '',
      document_printer_name: branch.document_printer_name || ''
    });
    resetLogoStates();
    if (branch.logo_path) {
      setLogoPreview(getImageUrl(branch.logo_path));
    }
    setModalMode('edit');
    setShowModal(true);
  };

  const handleSwitchToEdit = () => {
    if (selectedBranch) {
      setFormData({
        name: selectedBranch.name || '',
        address: selectedBranch.address || '',
        phone: selectedBranch.phone || '',
        email: selectedBranch.email || '',
        manager: selectedBranch.manager || '',
        vat_number: selectedBranch.vat_number || '',
        business_license: selectedBranch.business_license || '',
        dental_license: selectedBranch.dental_license || '',
        open_time: selectedBranch.open_time || '',
        close_time: selectedBranch.close_time || '',
        open_days: selectedBranch.open_days || '',
        language: selectedBranch.language || 'th',
        smartcard_enabled: selectedBranch.smartcard_enabled || false,
        vat_rate: selectedBranch.vat_rate ?? DEFAULT_VAT_RATE,
        mobile_phone: selectedBranch.mobile_phone || '',
        receipt_printer_name: selectedBranch.receipt_printer_name || '',
        label_printer_name: selectedBranch.label_printer_name || '',
        document_printer_name: selectedBranch.document_printer_name || ''
      });
      resetLogoStates();
      if (selectedBranch.logo_path) {
        setLogoPreview(getImageUrl(selectedBranch.logo_path));
      }
      setModalMode('edit');
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setRemoveLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogo(true);
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBranch) return;

    setIsSaving(true);
    setFormError('');
    try {
      // First update branch data
      await fetchWithAuth(`/api/branches/${selectedBranch.branch_id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...formData, remove_logo: removeLogo })
      });

      // Then upload logo if there's a new file
      if (logoFile) {
        const logoFormData = new FormData();
        logoFormData.append('logo', logoFile);

        const token = localStorage.getItem('token');
        // Raw fetch (not fetchWithAuth) because this is multipart, so the
        // response has to be checked here: a rejected upload used to be
        // swallowed and the modal closed as if the logo had been saved.
        const logoRes = await fetch(`${API_URL}/api/branches/${selectedBranch.branch_id}/logo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: logoFormData
        });
        if (!logoRes.ok) {
          const err = await logoRes.json().catch(() => ({}));
          throw Object.assign(new Error(err.message || 'Logo upload failed'), { code: 'LOGO_UPLOAD_FAILED' });
        }
      }

      setShowModal(false);
      fetchBranches();
      if (onBranchUpdate) {
        onBranchUpdate();
      }
      // Refresh header branch info if user updated their current branch
      if (currentBranch && selectedBranch.branch_id === currentBranch.branch_id && onCurrentBranchUpdate) {
        onCurrentBranchUpdate();
      }
    } catch (error) {
      console.error('Failed to save branch:', error);
      // The API's own messages are English. Codes it does define are
      // translated here; anything else falls back to a translated generic
      // rather than leaking raw English into a Thai front desk. This branch
      // deliberately removed exactly this class of dialog from PatientRecord
      // and DrugLabelSettings — the last one lived here.
      const byCode = {
        VAT_RATE_INVALID: print_lang('vat_rate_invalid', 'VAT rate must be a number between 0 and 100'),
        LOGO_UPLOAD_FAILED: print_lang('logo_upload_failed', 'Logo upload failed'),
      };
      setFormError(byCode[error.code] || print_lang('save_failed', 'Save failed'));
    } finally {
      setIsSaving(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedBranch(null);
    setFormData({});
    setFormError('');
    resetLogoStates();
  };

  const formatTime = (time) => {
    if (!time) return '-';
    return time.substring(0, 5); // Format HH:MM
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Building2 size={28} />
          {print_lang('branches', 'Branches')}
        </h1>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {branches.map((branch) => (
            <div key={branch.branch_id} className="card">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{branch.name}</h3>
                  {branch.manager && (
                    <span className="text-sm text-gray-500">{print_lang('manager', 'Manager')}: {branch.manager}</span>
                  )}
                </div>
              </div>

              <div className="space-y-2 text-sm text-gray-600 mb-4">
                {branch.address && (
                  <div className="flex items-start gap-2">
                    <MapPin size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-2">{branch.address}</span>
                  </div>
                )}
                {branch.phone && (
                  <div className="flex items-center gap-2">
                    <Phone size={16} className="text-gray-400" />
                    <span>{branch.phone}</span>
                  </div>
                )}
                {branch.email && (
                  <div className="flex items-center gap-2">
                    <Mail size={16} className="text-gray-400" />
                    <span>{branch.email}</span>
                  </div>
                )}
                {(branch.open_time || branch.close_time) && (
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-gray-400" />
                    <span>{formatTime(branch.open_time)} - {formatTime(branch.close_time)}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center pt-4 border-t">
                <span className="text-xs text-gray-400">
                  {print_lang('language', 'Language')}: {branch.language === 'th' ? 'ไทย' : 'English'}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleView(branch)}
                    className="p-2 hover:bg-gray-100 rounded"
                    title={print_lang('view', 'View')}
                  >
                    <Eye size={16} className="text-gray-600" />
                  </button>
                  <button
                    onClick={() => handleEdit(branch)}
                    className="p-2 hover:bg-gray-100 rounded"
                    title={print_lang('edit', 'Edit')}
                  >
                    <Edit size={16} className="text-blue-600" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {branches.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              {print_lang('no_branches', 'No branches found')}
            </div>
          )}
        </div>
      )}

      {/* View/Edit Modal */}
      {showModal && selectedBranch && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {modalMode === 'view'
                  ? print_lang('branch_details', 'Branch Details')
                  : print_lang('edit', 'Edit') + ' ' + print_lang('branch', 'Branch')
                }
              </h2>
              <button onClick={closeModal}>
                <X size={24} />
              </button>
            </div>

            {formError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4">
                {formError}
              </div>
            )}

            {modalMode === 'view' ? (
              /* View Mode */
              <div className="space-y-4">
                {/* Logo Display */}
                <div className="flex justify-center mb-4">
                  {selectedBranch.logo_path ? (
                    <img
                      src={getImageUrl(selectedBranch.logo_path)}
                      alt={selectedBranch.name}
                      className="h-24 object-contain"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-lg bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                      <Building2 size={32} className="text-gray-300" />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('branch_name', 'Branch Name')}</label>
                    <p className="font-medium">{selectedBranch.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('manager', 'Manager')}</label>
                    <p>{selectedBranch.manager || '-'}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-500 mb-1">{print_lang('address', 'Address')}</label>
                  <p>{selectedBranch.address || '-'}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('phone', 'Phone')}</label>
                    <p>{selectedBranch.phone || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('email', 'Email')}</label>
                    <p>{selectedBranch.email || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('vat_number', 'VAT Number')}</label>
                    <p className="font-mono">{selectedBranch.vat_number || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('dental_license', 'Dental License')}</label>
                    <p className="font-mono">{selectedBranch.dental_license || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('business_license', 'Business License')}</label>
                    <p className="font-mono">{selectedBranch.business_license || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('open_days', 'Open Days')}</label>
                    <p>{selectedBranch.open_days || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('open_time', 'Open Time')}</label>
                    <p>{formatTime(selectedBranch.open_time)}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('close_time', 'Close Time')}</label>
                    <p>{formatTime(selectedBranch.close_time)}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('language', 'Language')}</label>
                    <p>{selectedBranch.language === 'th' ? 'ไทย' : 'English'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('smartcard', 'Smart Card Reader')}</label>
                    <span className={`badge ${selectedBranch.smartcard_enabled ? 'badge-success' : 'badge-secondary'}`}>
                      {selectedBranch.smartcard_enabled ? print_lang('enabled', 'Enabled') : print_lang('disabled', 'Disabled')}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('dentist_limit', 'Dentist Limit')}</label>
                    <p className="font-medium text-blue-600">{selectedBranch.dentist_limit || 10}</p>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <button onClick={closeModal} className="btn btn-secondary">
                    {print_lang('close', 'Close')}
                  </button>
                  <button onClick={handleSwitchToEdit} className="btn btn-primary flex items-center gap-2">
                    <Edit size={18} />
                    {print_lang('edit', 'Edit')}
                  </button>
                </div>
              </div>
            ) : (
              /* Edit Mode */
              <form onSubmit={handleSubmit}>
                <div className="grid gap-4">
                  {/* Logo Upload */}
                  <div>
                    <label className="block text-sm font-medium mb-2">{print_lang('logo', 'Logo')}</label>
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        {logoPreview ? (
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            className="h-20 object-contain border rounded"
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                            <Building2 size={24} className="text-gray-300" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <input
                          type="file"
                          ref={logoInputRef}
                          onChange={handleLogoChange}
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => logoInputRef.current?.click()}
                          className="btn btn-secondary text-sm flex items-center gap-1"
                        >
                          <Upload size={16} />
                          {print_lang('upload_logo', 'Upload Logo')}
                        </button>
                        {(logoPreview || selectedBranch?.logo_path) && !removeLogo && (
                          <button
                            type="button"
                            onClick={handleRemoveLogo}
                            className="btn btn-secondary text-sm flex items-center gap-1 text-red-600"
                          >
                            <Trash2 size={16} />
                            {print_lang('remove', 'Remove')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('branch_name', 'Branch Name')} *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('manager', 'Manager')}</label>
                      <input
                        type="text"
                        value={formData.manager}
                        onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{print_lang('address', 'Address')} *</label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      rows={2}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('phone', 'Phone')}</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('mobile_phone', 'Mobile Phone')}</label>
                      <input
                        type="text"
                        className="w-full border rounded px-3 py-2"
                        value={formData.mobile_phone || ''}
                        onChange={(e) => setFormData({ ...formData, mobile_phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('email', 'Email')}</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('vat_number', 'VAT Number')}</label>
                      <input
                        type="text"
                        value={formData.vat_number}
                        onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('vat_rate', 'VAT Rate (%)')}</label>
                      <input
                        type="number" step="0.01" min="0" max="100"
                        className="w-full border rounded px-3 py-2"
                        value={formData.vat_rate ?? DEFAULT_VAT_RATE}
                        onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('dental_license', 'Dental License')}</label>
                      <input
                        type="text"
                        value={formData.dental_license}
                        onChange={(e) => setFormData({ ...formData, dental_license: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('business_license', 'Business License')}</label>
                      <input
                        type="text"
                        value={formData.business_license}
                        onChange={(e) => setFormData({ ...formData, business_license: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('open_days', 'Open Days')}</label>
                      <input
                        type="text"
                        value={formData.open_days}
                        onChange={(e) => setFormData({ ...formData, open_days: e.target.value })}
                        placeholder="Mon-Sat"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('open_time', 'Open Time')}</label>
                      <input
                        type="time"
                        value={formData.open_time}
                        onChange={(e) => setFormData({ ...formData, open_time: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('close_time', 'Close Time')}</label>
                      <input
                        type="time"
                        value={formData.close_time}
                        onChange={(e) => setFormData({ ...formData, close_time: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('language', 'Language')}</label>
                      <select
                        value={formData.language}
                        onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                      >
                        <option value="th">ไทย</option>
                        <option value="en">English</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.smartcard_enabled}
                        onChange={(e) => setFormData({ ...formData, smartcard_enabled: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium">{print_lang('enable_smartcard', 'Enable Smart Card Reader')}</span>
                    </label>
                  </div>

                  <div className="col-span-2 mt-4 pt-4 border-t">
                    <h3 className="font-semibold mb-1">{print_lang('printer_settings', 'Printer Settings')}</h3>
                    <p className="text-sm text-gray-500 mb-3">
                      {print_lang('printer_name_hint', 'Leave blank to use the browser print dialog')}
                    </p>

                    {/* The agent is per-PC. Saying so here is the difference
                        between "printing is broken" and "printing is falling
                        back on THIS machine" — the names below still save, and
                        a PC that does have the agent will use them. Only shown
                        once the probe has answered, so it does not flash on
                        every open. */}
                    {agentProbed && !agentUp && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-3 text-sm flex items-start gap-2">
                        <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">
                            {print_lang('print_agent_not_found', 'Print agent not found on this PC')}
                          </p>
                          <p className="mt-0.5">
                            {print_lang('print_agent_not_found_hint',
                              'Printing will use the browser print dialog. Install and start the print agent on this PC for silent printing. Printer names typed below are still saved.')}
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        ['receipt_printer_name', 'receipt_printer', 'Receipt Printer'],
                        ['label_printer_name', 'label_printer', 'Label Printer'],
                        ['document_printer_name', 'document_printer', 'Document Printer (A4)'],
                      ].map(([field, key, fallback]) => (
                        <div key={field}>
                          <label className="block text-sm font-medium mb-1">{print_lang(key, fallback)}</label>
                          {usePrinterDropdown ? (
                            <select className="w-full border rounded px-3 py-2"
                                    value={formData[field] || ''}
                                    onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}>
                              <option value="">{print_lang('use_browser_dialog', 'Use the browser print dialog')}</option>
                              {/* A name saved from another PC may not exist here.
                                  Keep it as an option rather than silently
                                  resetting the branch to "browser dialog" the
                                  moment someone opens the form on a different
                                  machine. */}
                              {formData[field] && !agentPrinters.includes(formData[field]) && (
                                <option value={formData[field]}>{formData[field]}</option>
                              )}
                              {agentPrinters.map((p) => (
                                <option key={p} value={p}>{p}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              className="w-full border rounded px-3 py-2"
                              value={formData[field] || ''}
                              onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <button type="button" onClick={closeModal} className="btn btn-secondary">
                    {print_lang('cancel', 'Cancel')}
                  </button>
                  <button type="submit" disabled={isSaving} className="btn btn-primary flex items-center gap-2">
                    <Save size={18} />
                    {isSaving ? print_lang('saving', 'Saving...') : print_lang('save', 'Save')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Branches;
